import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postPickerAccess, useAdminPickersCache } from '../../../api/admin-pickers';
import type { PickerAccessAction } from '../../../api/admin-pickers';
import { publishToast } from '../../../components/feedback';

/**
 * Disable / enable orchestration for the picker drawer (AUT-400).
 *
 * On success: patch the drawer from the response, invalidate
 * `['admin','pickers']` (list + totals), and refetch the detail ONCE more
 * after {@link LINK_SWEEP_SETTLE_MS}. The server answers before the async
 * `picker.disabled` listener has revoked the links, so the response's
 * `activeLinkCount` is usually stale; the late refetch settles it. The UI
 * also labels a disabled picker's links «закрыты» meanwhile.
 *
 * Errors map by `code` (the request opts out of the shared 403 / 409
 * toast, so exactly ONE toast shows): `picker_not_found`, `validation_*`,
 * 403, else generic. Any failure re-syncs the cache.
 */
export const LINK_SWEEP_SETTLE_MS = 1500;

const errorKey = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    const code = (err.response?.data as { code?: string } | undefined)?.code;
    if (code === 'picker_not_found') return 'pickers.errors.picker_not_found';
    if (code?.startsWith('validation_')) return 'pickers.errors.validation';
    if (err.response?.status === 403) return 'errors.forbidden';
  }
  return 'pickers.errors.generic';
};

export interface UsePickerAccessActionsResult {
  busy: boolean;
  /** Resolves true on success, false on error. */
  run: (id: string, action: PickerAccessAction, reason: string) => Promise<boolean>;
}

export const usePickerAccessActions = (): UsePickerAccessActionsResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const cache = useAdminPickersCache();
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const run = async (id: string, action: PickerAccessAction, reason: string): Promise<boolean> => {
    setBusy(true);
    try {
      const detail = await postPickerAccess(id, action, reason);
      cache.patchDetail(id, detail);
      await cache.invalidate();
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        void cache.refetchDetail(id);
      }, LINK_SWEEP_SETTLE_MS);
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t(`pickers.${action}Dialog.successToast`),
      });
      return true;
    } catch (err: unknown) {
      await cache.invalidate();
      publishToast({ severity: 'error', durationMs: 6000, message: t(errorKey(err)) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
